import { Theme } from "@mui/material/styles";

const DEFAULT_BORDER_RADIUS = 10;
const DEFAULT_INPUT_HEIGHT = 56;

const getThemedComponents = (
  theme: Theme
): { components: Theme["components"] } => {
  return {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*": {
            boxSizing: "border-box",
          },
          html: {
            height: "100%",
            width: "100%",
            fontFamily: "'Lato', sans-serif",
            overflowX: "hidden",
          },
          body: {
            height: "100vh",
            minHeight: "100vh",
            margin: 0,
            padding: 0,
            fontFamily: "'Lato', sans-serif",
            overflowX: "hidden",
            position: "relative",
            // The app's own scrollbar, from _layout.scss. Webkit-only there too.
            "&::-webkit-scrollbar": { width: "0.5em", height: "0.5em" },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.grey[600],
            },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.grey[400],
            },
          },
          "#root": {
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "100vh",
          },
          ".MuiCardHeader-action": {
            alignSelf: "center !important",
          },
          p: {
            fontFamily: "'Lato', sans-serif",
            margin: "0 0 1em",
            lineHeight: 1.4,
            // Load-bearing: the bare <p> that render user-written text rely on
            // this to keep the newlines the author typed.
            whiteSpace: "pre-wrap",
          },
          "ul, ol": { listStyleType: "none", padding: 0, margin: 0 },
          img: { fontSize: "0.5em", color: theme.palette.secondary.main },
          /**
           * Wave 13 moved the Sass global layer here. Everything below styles
           * an element that is deliberately *not* a MUI component — react-router
           * renders a bare <a>, react-confirm-alert renders bare <button>s, and
           * a handful of forms still use native controls. Each rule keeps the
           * `:not(.Mui…)` guard it had in _base.scss so MUI's own components are
           * untouched; the hexes are theme values now rather than $variables.
           */
          "h1:not(.MuiTypography-root)": {
            fontSize: "3.2em",
            fontWeight: 500,
            letterSpacing: "1px",
            lineHeight: 1.4,
            margin: "0 0 0.4em",
            color: theme.palette.secondary.main,
          },
          "h2:not(.MuiTypography-root)": {
            fontSize: "1.5em",
            fontWeight: 500,
            letterSpacing: "0.7px",
            lineHeight: 1.4,
            margin: "0 0 0.4em",
            color: theme.palette.secondary.main,
          },
          "h3:not(.MuiTypography-root)": {
            fontSize: "1.2em",
            fontWeight: 500,
            letterSpacing: "0.7px",
            lineHeight: 1.4,
            margin: "0 0 0.4em",
            color: theme.palette.secondary.main,
          },
          "h4:not(.MuiTypography-root)": {
            fontSize: "1.1em",
            fontWeight: 500,
            letterSpacing: "0.7px",
            margin: "0 0 0.4em",
            color: theme.palette.secondary.main,
          },
          "h5:not(.MuiTypography-root)": {
            fontSize: "1em",
            fontWeight: 500,
            letterSpacing: "0.7px",
            margin: "0 0 0.4em",
            color: theme.palette.secondary.main,
          },
          "h6:not(.MuiTypography-root)": {
            display: "block",
            fontSize: "0.9em",
            fontWeight: 500,
            letterSpacing: "0.7px",
            textTransform: "uppercase",
            color: theme.palette.grey[600],
            margin: 0,
          },
          "a:not(.MuiMenuItem-root):not(.MuiLink-root)": {
            color: theme.palette.info[50],
            textDecoration: "none",
            cursor: "pointer",
            lineHeight: "110%",
            "&:hover": { color: theme.palette.secondary.main },
            "&:focus": {
              borderBottom: `2px solid ${theme.palette.primary[300]}`,
              outline: "none",
            },
          },
          "button:not(.MuiButtonBase-root, .MuiIconButton-root, .MuiButton-root, .MuiCardActionArea-root)":
            {
              backgroundColor: "transparent",
              letterSpacing: "0.7px",
              cursor: "pointer",
              borderRadius: 3,
              boxSizing: "border-box",
              fontSize: "0.8em",
              "&:focus": {
                borderBottom: `2px solid ${theme.palette.primary[300]}`,
                outline: "none",
              },
              "&:disabled, &:disabled:hover": {
                color: theme.palette.grey[600],
                backgroundColor: theme.palette.grey[50],
                borderColor: theme.palette.grey[300],
                cursor: "not-allowed",
              },
            },
          "input:not(.MuiInputBase-input), textarea:not(.MuiInputBase-input)": {
            border: `0.5px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.common.white,
            color: theme.palette.common.black,
            fontFamily: "'Lato', sans-serif",
            fontWeight: 400,
            letterSpacing: "1px",
            lineHeight: "1.5em",
            padding: "10px 6px",
            boxSizing: "border-box",
            cursor: "text",
            // 16px keeps iOS Safari from zooming the viewport on focus.
            fontSize: "16px !important",
            "&::placeholder": { color: theme.palette.grey[800] },
            "&:focus": {
              outline: 0,
              borderBottom: `3px solid ${theme.palette.primary.main}`,
            },
            "&:disabled": {
              backgroundColor: theme.palette.grey[300],
              color: theme.palette.grey[600],
              cursor: "not-allowed",
            },
          },
          "textarea:not(.MuiInputBase-input)": {
            whiteSpace: "pre-line",
            overflow: "auto",
            margin: "5px auto",
          },
          select: {
            fontFamily: "'Lato', sans-serif",
            fontWeight: 400,
            letterSpacing: "1px",
            lineHeight: "1.5em",
            border: `0.5px solid ${theme.palette.grey[300]}`,
            borderRadius: 0,
            WebkitAppearance: "none",
            cursor: "pointer",
            color: theme.palette.common.black,
            padding: "10px 20px 10px 6px",
            margin: "5px auto",
            boxSizing: "border-box",
            background: `${theme.palette.common.white} url(/images/top_dropdown.png) no-repeat 95% center`,
            backgroundSize: "10px 5px",
            "&:disabled": { backgroundImage: "none" },
            "&::-ms-expand": { display: "none" },
            "&:focus": {
              outline: 0,
              borderBottom: `3px solid ${theme.palette.primary.main}`,
              fontSize: "16px !important",
            },
          },
          "label:not(.MuiInputLabel-root)": {
            marginTop: 8,
            marginBottom: 4,
            lineHeight: "110%",
            cursor: "pointer",
          },
          "input:-webkit-autofill": {
            transition: "background-color 5000s ease-in-out 0s",
          },
          /**
           * react-confirm-alert injects its own markup with its own class names,
           * so these never appeared in any `className` in the repo and every
           * class-usage sweep read them as dead. They are not: six components
           * call `confirmAlert()`.
           */
          ".react-confirm-alert-overlay": {
            zIndex: "999 !important",
            transform: "translateZ(1000px)",
          },
          ".react-confirm-alert-body": {
            borderRadius: "0 !important",
            fontFamily: "'Lato', sans-serif !important",
            h1: { textTransform: "uppercase" },
          },
          ".react-confirm-alert-button-group": {
            justifyContent: "flex-end !important",
            button: {
              borderRadius: "3px !important",
              "&:first-of-type": {
                background: theme.palette.common.white,
                border: `1px solid ${theme.palette.secondary.main}`,
                color: theme.palette.secondary.main,
              },
              "&:last-child": {
                background: theme.palette.info[50],
                border: `1px solid ${theme.palette.info[50]}`,
              },
            },
          },
          "&.MuiTypography-root": {
            padding: 0,
            border: 0,
            outline: 0,
            verticalAlign: "baseline",
            fontStyle: "normal",
          },
          "&.MuiTypography-root.capitalize": {
            textTransform: "capitalize",
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: "0px",
            paddingRight: "0px",
            paddingTop: "0px",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: theme?.palette?.common.white,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            paddingLeft: "0 !important",
            paddingRight: "0 !important",
          },
        },
      },
      /**
       * Tooltip specification can be found in below link. Should be applied for all components
       * @reference
       *  - https://rebel-base.atlassian.net/wiki/spaces/REBELBASE/pages/23232513/Rebel+Dashboard+Reference+codes
       */
      MuiTooltip: {
        styleOverrides: {
          tooltip: theme.unstable_sx({
            p: 2,
            maxWidth: 256,
            background: theme?.palette?.grey["600"],
            borderRadius: 1,
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ ownerState }) => {
            const state = {
              height: DEFAULT_INPUT_HEIGHT,
              minWidth: 248,
              borderRadius: DEFAULT_BORDER_RADIUS,
            };
            if (ownerState.size === "small") {
              state.height = 44;
              state.minWidth = 102;
              state.borderRadius = 5;
            } else if (ownerState.size === "medium") {
              state.minWidth = 174;
            }
            return theme.unstable_sx(state);
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          MenuProps: {
            slotProps: {
              paper: {
                sx: {
                  "& .MuiMenuItem-root": {
                    minHeight: DEFAULT_INPUT_HEIGHT,
                  },
                },
              },
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: ({ ownerState }) => {
            const state = {
              minHeight: DEFAULT_INPUT_HEIGHT,
              minWidth: 320,
            };
            if (ownerState.size === "small") {
              state.minHeight = 32;
              state.minWidth = 128;
            }
            return theme.unstable_sx({
              ...state,
              "&.MuiOutlinedInput-root": {
                borderRadius: DEFAULT_BORDER_RADIUS,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme?.palette?.grey?.[600],
                },
              },
            });
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: "9px",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: theme.unstable_sx({
            pb: 0,
            pt: 0,
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: theme.unstable_sx({
            padding: 0,
            margin: 0,
          }),
        },
      },
      MuiList: {
        styleOverrides: {
          root: theme.unstable_sx({
            p: theme.spacing(0),
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: "40px",
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            "&.rebel-disabled": {
              "& .MuiTabs-indicator": {
                backgroundColor: theme?.palette?.action?.disabledBackground,
              },
              "& .Mui-selected": {
                color: theme?.palette?.action?.disabled,
              },
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: theme.unstable_sx({
            color: theme?.palette?.grey["600"],
            minHeight: 80,
            minWidth: 96,
          }),
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: theme.unstable_sx({
            backgroundColor: "#ecf0f2",
            borderRadius: DEFAULT_BORDER_RADIUS,
          }),
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            "&:last-child": {
              paddingBottom: "1rem",
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            "& .MuiMenuItem-root": {
              "&.Mui-selected": {
                color: theme?.palette?.common?.white,
                backgroundColor: theme?.palette?.primary?.main,
              },
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: theme.unstable_sx({
            cursor: "pointer",
            "&:hover": {
              fontWeight: 700,
            },
          }),
        },
        variants: [
          {
            props: { underline: "always" },
            style: { textDecorationColor: "currentColor" },
          },
          {
            props: { underline: "hover" },
            style: {
              "&:hover": { textDecorationColor: "currentColor" },
            },
          },
        ],
      },
      MuiPaper: {
        styleOverrides: {
          root: theme.unstable_sx({
            backgroundColor: theme?.palette?.common?.white,
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: theme.unstable_sx({
            borderRadius: 1,
            typography: "body1",
            borderColor: theme?.palette?.grey["600"],
            boxShadow: 1,
            color: theme?.palette?.secondary?.main,
          }),
        },
      },
    },
  };
};

export default getThemedComponents;
